import React, { useState, useEffect } from "react";
import {
  FaPlus,
  FaUser,
  FaUserShield,
  FaUserTie,
  FaTools,
  FaHardHat,
  FaChartLine,
  FaCalculator,
  FaWarehouse,
  FaCode,
  FaIndustry,
  FaUsers,
  FaEnvelope,
  FaTrash,
  FaCheckCircle,
  FaTimesCircle,
} from "react-icons/fa";
import { MdEmail, MdPhone, MdBusiness, MdCalendarToday } from "react-icons/md";
import FormatDateString from "../Utils/Date/FormatDateString";
import { Pagination } from "../atoms";
import { AppLink, ListView, ListToolbar } from "../shared";
import { useDebounce } from "../../Hooks/useDebounce";
import mascotNoData from "../../Assets/icons/okkali_mascot_nodata.png";
import { useResendInvitationMutation, useDeleteUserByIDMutation } from "../../store";
import PasswordConfirmModal from "../Utils/PasswordConfirmModal";
import useAuth from "../../Hooks/useAuth";
import { useAlert } from "../../context/AlertContext";
import { getRoleLabel, getRoleBadgeClass } from "../../utils/roleBadge";

const getRoleName = (role) => {
  const roles = {
    admin: "Sistem Yöneticisi",
    developer: "Geliştirici",
    management: "Yönetim",
    storage: "Depo",
    dealer: "Bayi",
    playmaker: "Plasiyer",
    service: "Servis",
    accounting: "Muhasebe",
    depo: "Depo", // bayi (dealer) subRole'ü
    manufacturer: "Üretim",
    customer: "Müşteri",
    enduser: "Son Kullanıcı",
    machineworker: "Makine İşçisi",
  };
  return roles[role] || role;
};

const getRoleColor = (role) => {
  const colors = {
    admin: "bg-purple-100 text-purple-800",
    developer: "bg-sky-100 text-sky-800",
    management: "bg-blue-100 text-blue-800",
    storage: "bg-slate-100 text-slate-800",
    dealer: "bg-violet-100 text-violet-800",
    playmaker: "bg-emerald-100 text-emerald-800",
    service: "bg-indigo-100 text-indigo-800",
    accounting: "bg-amber-100 text-amber-800",
    depo: "bg-sky-100 text-sky-800",
    manufacturer: "bg-orange-100 text-orange-800",
    customer: "bg-teal-100 text-teal-800",
    enduser: "bg-gray-100 text-gray-800",
    machineworker: "bg-green-100 text-green-800",
  };
  return colors[role] || "bg-gray-100 text-gray-800";
};

const getRoleIcon = (role) => {
  const icons = {
    admin: FaUserShield,
    developer: FaCode,
    management: FaUserTie,
    storage: FaUserShield,
    dealer: FaUserTie,
    playmaker: FaChartLine,
    service: FaTools,
    accounting: FaCalculator,
    depo: FaWarehouse,
    manufacturer: FaIndustry,
    customer: FaUser,
    enduser: FaUser,
    machineworker: FaHardHat,
  };
  return icons[role] || FaUser;
};

/** Ana rol filtreleri — bu tab yalnızca depo/bayi/müşteri (makine işçisi + son kullanıcı hariç) */
const ROLE_FILTERS = [
  { id: "all", label: "Tümü", icon: FaUsers },
  { id: "storage", label: "Depo", icon: FaUserShield },
  { id: "dealer", label: "Bayi", icon: FaUserTie },
  { id: "customer", label: "Müşteri", icon: FaUser },
  // #203 — panelde başka listede aranamayan roller.
  { id: "enduser", label: "Son Kullanıcı", icon: FaUser },
  { id: "machineworker", label: "Makine İşçisi", icon: FaHardHat },
];

// Loading Skeleton - Desktop
const UserSkeleton = () => (
  <tr className="bg-white animate-pulse">
    <td className="px-4 py-3">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 bg-gray-200 rounded-lg"></div>
        <div>
          <div className="h-4 bg-gray-200 rounded w-28 mb-1"></div>
          <div className="h-3 bg-gray-200 rounded w-16"></div>
        </div>
      </div>
    </td>
    <td className="px-4 py-3">
      <div className="space-y-1">
        <div className="h-3 bg-gray-200 rounded w-36"></div>
        <div className="h-3 bg-gray-200 rounded w-24"></div>
      </div>
    </td>
    <td className="px-4 py-3"><div className="h-3 bg-gray-200 rounded w-24"></div></td>
    <td className="px-4 py-3"><div className="h-6 bg-gray-200 rounded w-20"></div></td>
    <td className="px-4 py-3"><div className="h-3 bg-gray-200 rounded w-16"></div></td>
    <td className="px-4 py-3"><div className="h-3 bg-gray-200 rounded w-20"></div></td>
    <td className="px-4 py-3"><div className="h-7 bg-gray-200 rounded w-24"></div></td>
  </tr>
);

// Loading Skeleton - Mobile
const MobileUserSkeleton = () => (
  <div className="animate-pulse bg-white px-3.5 py-3 border-b border-gray-200">
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 bg-gray-200 rounded-lg"></div>
        <div>
          <div className="h-4 bg-gray-200 rounded w-28 mb-1"></div>
          <div className="h-3 bg-gray-200 rounded w-20"></div>
        </div>
      </div>
      <div className="h-5 bg-gray-200 rounded-md w-14"></div>
    </div>
    <div className="flex gap-2">
      <div className="h-3 bg-gray-200 rounded w-40"></div>
    </div>
  </div>
);

const UserListWithSearch = ({
  data,
  total = 0,
  totalPages = 1,
  currentServerPage = 1,
  canViewUserDetail,
  onAddNew,
  isLoading = false,
  token,
  onSearch,
  onRefetch,
  isAdmin = false,
  hasPermission = () => false,
}) => {
  // Ayrı arama alanları: İsim / E-posta / Firma / Rol(subRole)
  const [searchName, setSearchName] = useState("");
  const [searchEmail, setSearchEmail] = useState("");
  const [searchFirm, setSearchFirm] = useState("");
  const [searchSubRole, setSearchSubRole] = useState("");
  const dName = useDebounce(searchName, 300);
  const dEmail = useDebounce(searchEmail, 300);
  const dFirm = useDebounce(searchFirm, 300);
  const dSubRole = useDebounce(searchSubRole, 300);
  const [activeFilter, setActiveFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Aktif arama parametreleri (server'a gönderilir)
  const buildSearchParams = (page = currentPage) => ({
    name: dName,
    email: dEmail,
    firmName: dFirm,
    subRole: dSubRole,
    role: activeFilter,
    page,
    limit: itemsPerPage,
  });
  const [deleteModal, setDeleteModal] = useState({ open: false, user: null });

  const { auth } = useAuth();
  const twoFactorEnabled = auth?.twoFactorEnabled === true;
  const { showSuccess, showError } = useAlert();

  const [resendInvitation, { isLoading: resendLoading }] = useResendInvitationMutation();
  const [deleteUserByID, { isLoading: deleteLoading }] = useDeleteUserByIDMutation();

  // Server-side search: debounced alanlar + filtre + sayfa
  useEffect(() => {
    onSearch?.(buildSearchParams());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dName, dEmail, dFirm, dSubRole, activeFilter, currentPage]);

  // Arama veya filtre değişince sayfa 1'e dön
  useEffect(() => {
    setCurrentPage(1);
  }, [dName, dEmail, dFirm, dSubRole, activeFilter]);

  const handleResendInvitation = async (e, user) => {
    e.stopPropagation();
    if (!token || !user?._id) return;
    try {
      await resendInvitation({ userId: user._id, token }).unwrap();
      showSuccess("Davet e-postası tekrar gönderildi.");
      onSearch?.(buildSearchParams());
    } catch (err) {
      showError(err?.data?.message || "Davet e-postası gönderilemedi.");
    }
  };

  const openDeleteModal = (e, user) => {
    e.stopPropagation();
    setDeleteModal({ open: true, user });
  };

  const closeDeleteModal = () => setDeleteModal({ open: false, user: null });

  // Ortak PasswordConfirmModal onConfirm ile { password, passwordConfirm, code? } gönderir.
  const handleConfirmDelete = async ({ password, passwordConfirm, code }) => {
    if (!token || !deleteModal.user?._id) return;
    try {
      await deleteUserByID({
        input: {
          id: deleteModal.user._id,
          password,
          passwordConfirm,
          ...(code ? { code } : {}),
        },
        token,
      }).unwrap();
      showSuccess("Kullanıcı silindi.");
      closeDeleteModal();
      onSearch?.(buildSearchParams());
    } catch (err) {
      showError(err?.data?.message || "Silme işlemi başarısız.");
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    const tableContainer = document.querySelector(".overflow-y-auto");
    if (tableContainer) tableContainer.scrollTop = 0;
  };

  // Satır link DEĞİL — detaya yalnızca avatar + isim linkinden gidilir
  // (hover'da altı çizilir). Satır geneli overlay kaldırıldı.
  const rowClassName = () =>
    "relative bg-white even:bg-gray-50/70 hover:bg-gray-100/70 transition-colors duration-200 border-b border-gray-100";

  // Aksiyon butonları — masaüstü ve mobilde ortak (boyut className'i parametrik).
  const renderActions = (user, size) => {
    const mobile = size === "mobile";
    // Masaüstü/mobil eski görünüm birebir (padding/rounded/icon farkları korunur).
    const btn = mobile
      ? "gap-1 px-2 py-1 text-xs rounded-md"
      : "gap-1.5 px-2 py-1.5 text-xs rounded-lg";
    const iconCls = mobile ? "w-2.5 h-2.5" : "w-3 h-3";
    return (
      <>
        {user.accountActivated === false && token && hasPermission("users.resendInvitation") && (
          <button
            type="button"
            onClick={(e) => handleResendInvitation(e, user)}
            disabled={resendLoading}
            className={`inline-flex items-center ${btn} font-medium text-amber-700 bg-amber-50 border border-amber-200 hover:bg-amber-100 disabled:opacity-50`}
          >
            <FaEnvelope className={iconCls} />
            Tekrar mail gönder
          </button>
        )}
        {hasPermission("users.delete") && (
          <button
            type="button"
            onClick={(e) => openDeleteModal(e, user)}
            disabled={deleteLoading}
            className={`inline-flex items-center ${btn} font-semibold text-white bg-red-600 shadow-sm hover:bg-red-700 hover:shadow-md active:bg-red-800 transition-all disabled:opacity-50`}
          >
            <FaTrash className={iconCls} />
            Sil
          </button>
        )}
      </>
    );
  };

  // Durum rozeti — ikonlu (masaüstü + mobil ortak).
  const renderStatusBadge = (user) => {
    const active = user.accountActivated !== false;
    // Davet bağlantısının 7 günü dolmuş — "Tekrar mail gönder" ile yenilenir (#223).
    const expired = !active && user.invitationExpired === true;
    const Icon = active ? FaCheckCircle : FaTimesCircle;
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
          active
            ? "bg-emerald-50 text-emerald-700"
            : expired
              ? "bg-red-50 text-red-700"
              : "bg-amber-50 text-amber-700"
        }`}
        title={expired ? "Davet bağlantısının süresi doldu — tekrar mail gönderin" : undefined}
      >
        <Icon className="w-3 h-3" />
        {/* accountActivated=false: davet linkiyle şifre henüz belirlenmedi;
            bu hesap giriş yapamaz (#164 davet kapısı) */}
        {active ? "Aktif" : expired ? "Davet süresi doldu" : "Davet bekliyor"}
      </span>
    );
  };

  // Ana rol tipi rozeti (Depo/Bayi/Müşteri/Makine İşçisi) — Firma sütununda gösterilir.
  const renderMainRoleBadge = (user) => {
    const RoleIcon = getRoleIcon(user.role);
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full w-fit ${getRoleColor(
          user.role
        )}`}
      >
        <RoleIcon className="w-3 h-3" />
        {getRoleName(user.role)}
      </span>
    );
  };

  // Masaüstü tablo kolonları (ListView config).
  // Sıra: Kullanıcı, Rol, Firma, Durum, İletişim, Kayıt Tarihi, İşlem
  const columns = [
    {
      key: "user",
      label: "KULLANICI",
      // Avatar + isim tek link — birine hover'da ikisi birden link
      // görünümüne geçer (cihaz/firma listesi deseni). relative z-10:
      // satırdaki absolute overlay'in üstünde kalsın.
      render: (user) => {
        const inner = (
          <>
            <div className="w-9 h-9 rounded-lg bg-gradient-to-r from-indigo-500 to-blue-600 flex items-center justify-center text-white font-bold text-xs shrink-0 border border-transparent transition-all group-hover:ring-2 group-hover:ring-indigo-300">
              {user.name?.charAt(0)}
              {user.lastName?.charAt(0)}
            </div>
            <div>
              <div className="text-sm font-semibold text-gray-900 transition-colors group-hover:text-indigo-700 group-hover:underline underline-offset-2 decoration-indigo-400">
                {user.name} {user.lastName}
              </div>
            </div>
          </>
        );
        return canViewUserDetail ? (
          <AppLink
            to={`/Anasayfa/Kullanıcı/${user._id}`}
            state={{ user, fromPage: "Kullanıcı" }}
            className="group relative z-10 flex items-center gap-2.5 w-fit max-w-full"
            title="Kullanıcıya git"
          >
            {inner}
          </AppLink>
        ) : (
          <div className="flex items-center gap-2.5">{inner}</div>
        );
      },
    },
    {
      key: "role",
      label: "ROL",
      render: (user) =>
        user.subRole ? (
          // Renk ortak util'den — subRole şablon adı olduğu için özel adlı
          // şablonlar da birbirinden ayrışsın (bkz. utils/roleBadge.js).
          <span
            className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold border ${getRoleBadgeClass(
              user.role,
              user.subRole
            )}`}
          >
            {getRoleLabel(user.role, user.subRole)}
          </span>
        ) : (
          <span className="text-xs font-medium text-gray-400">-</span>
        ),
    },
    {
      key: "firm",
      label: "FİRMA",
      render: (user) => (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1.5">
            <MdBusiness className="text-gray-400 w-3 h-3 shrink-0" />
            <span className="text-xs text-gray-900">{user.firm?.firmName || "-"}</span>
          </div>
          {renderMainRoleBadge(user)}
        </div>
      ),
    },
    {
      key: "status",
      label: "DURUM",
      render: (user) => renderStatusBadge(user),
    },
    {
      key: "contact",
      label: "İLETİŞİM",
      render: (user) => (
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <MdEmail className="text-gray-400 w-3 h-3" />
            <span className="text-xs text-gray-900 truncate max-w-[180px]">{user.email}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <MdPhone className="text-gray-400 w-3 h-3" />
            <span className="text-xs text-gray-900">{user.tel || "-"}</span>
          </div>
        </div>
      ),
    },
    {
      key: "date",
      label: "KAYIT TARİHİ",
      render: (user) => (
        <div className="flex items-center gap-1.5">
          <MdCalendarToday className="text-gray-400 w-3 h-3" />
          <span className="text-xs text-gray-900">
            {user.createdInfo ? FormatDateString(user.createdInfo) : "-"}
          </span>
        </div>
      ),
    },
    {
      key: "actions",
      label: "İŞLEM",
      cellClassName: "relative z-10",
      render: (user) => (
        <div className="flex flex-wrap items-center gap-1.5">
          {renderActions(user, "desktop")}
          {!hasPermission("users.delete") &&
            !hasPermission("users.resendInvitation") &&
            !hasPermission("users.edit") && (
              <span className="text-xs text-gray-400">-</span>
            )}
        </div>
      ),
    },
  ];

  // Mobil kart (ListView config). Kart link değil — yalnızca avatar + isim +
  // e-posta bloğu linktir.
  const renderMobileCard = (user) => {
    const mobileNameInner = (
      <>
        <div className="w-9 h-9 rounded-lg bg-gradient-to-r from-indigo-500 to-blue-600 flex items-center justify-center text-white font-bold text-xs shrink-0 border border-transparent transition-all group-hover:ring-2 group-hover:ring-indigo-300 group-active:ring-2 group-active:ring-indigo-300">
          {user.name?.charAt(0)}
          {user.lastName?.charAt(0)}
        </div>
        <div className="min-w-0">
          <div className="text-sm font-bold text-gray-900 truncate group-hover:text-indigo-700 group-hover:underline underline-offset-2 decoration-indigo-400">
            {user.name} {user.lastName}
          </div>
          <div className="text-xs text-gray-500 truncate group-hover:underline underline-offset-2">{user.email}</div>
        </div>
      </>
    );
    return (
      <div className="relative bg-white px-3.5 py-3 active:bg-gray-50 transition-colors border-b border-gray-200">
        {/* Üst: İsim + Rol */}
        <div className="flex items-start justify-between gap-2 mb-2">
          {canViewUserDetail ? (
            <AppLink
              to={`/Anasayfa/Kullanıcı/${user._id}`}
              state={{ user, fromPage: "Kullanıcı" }}
              className="group flex items-center gap-2 min-w-0"
              title="Kullanıcıya git"
            >
              {mobileNameInner}
            </AppLink>
          ) : (
            <div className="flex items-center gap-2 min-w-0">{mobileNameInner}</div>
          )}
          <div className="flex flex-col items-end gap-1 shrink-0">
            {user.subRole && (
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold border ${getRoleBadgeClass(
                  user.role,
                  user.subRole
                )}`}
              >
                {getRoleLabel(user.role, user.subRole)}
              </span>
            )}
            {renderStatusBadge(user)}
          </div>
        </div>

        {/* Aksiyonlar */}
        <div className="relative z-10 mt-2 flex flex-wrap gap-1.5">
          {renderActions(user, "mobile")}
        </div>

        {/* Alt: Firma + ana rol + telefon */}
        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
          {user.firm?.firmName && (
            <span className="flex items-center gap-1 truncate">
              <MdBusiness className="w-2.5 h-2.5 text-gray-400 shrink-0" />
              {user.firm.firmName}
            </span>
          )}
          {renderMainRoleBadge(user)}
          {user.tel && (
            <span className="flex items-center gap-1 truncate">
              <MdPhone className="w-2.5 h-2.5 text-gray-400 shrink-0" />
              {user.tel}
            </span>
          )}
        </div>
      </div>
    );
  };

  const emptyNode = (
    <div className="flex flex-col items-center justify-center py-12 sm:py-16 select-none">
      <img
        src={mascotNoData}
        alt="Kullanıcı bulunamadı"
        className="w-28 h-28 sm:w-36 sm:h-36 md:w-44 md:h-44 object-contain mb-4 sm:mb-6 drop-shadow-lg"
        draggable={false}
      />
      <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-700 mb-1">
        Kullanıcı Bulunamadı
      </h3>
      <p className="text-sm sm:text-base text-gray-400 max-w-sm text-center">
        Aradığınız kriterlere uygun kullanıcı bulunamadı. Farklı bir arama terimi deneyin.
      </p>
    </div>
  );

  const footerNode =
    (total > 0 || isLoading) && totalPages > 1 ? (
      <div className="shrink-0 border-t border-gray-200 bg-white">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          itemsPerPage={itemsPerPage}
          totalItems={total}
          showInfo={true}
          disabled={isLoading}
        />
      </div>
    ) : null;

  return (
    <div className="flex flex-col h-full">
      {/* List Container */}
      <div className="bg-white h-full flex flex-col">
        {/* Header & Filters */}
        <ListToolbar
          icon={FaUsers}
          title="Kullanıcı Yönetimi"
          subtitle="Kullanıcıları yönetin ve takip edin"
          count={total}
          countLabel="Kullanıcı"
          countIcon={FaUsers}
          isLoading={isLoading}
          actionItems={
            onAddNew
              ? [
                  {
                    label: "Yeni Kullanıcı",
                    mobileLabel: "Yeni Kullanıcı Oluştur",
                    icon: FaPlus,
                    onClick: onAddNew,
                    variant: "primary",
                  },
                ]
              : []
          }
          filterTabs={{
            filters: ROLE_FILTERS,
            activeFilter,
            onChange: setActiveFilter,
            totalCount: total,
            isLoading,
            color: "indigo",
          }}
          searchFields={[
            { placeholder: "İsim", value: searchName, onChange: (e) => setSearchName(e.target.value) },
            { placeholder: "E-posta", value: searchEmail, onChange: (e) => setSearchEmail(e.target.value) },
            { placeholder: "Firma", value: searchFirm, onChange: (e) => setSearchFirm(e.target.value) },
            { placeholder: "Rol", value: searchSubRole, onChange: (e) => setSearchSubRole(e.target.value) },
          ]}
        />

        <ListView
          columns={columns}
          data={data}
          rowKey="_id"
          rowClassName={rowClassName}
          renderMobileCard={renderMobileCard}
          isLoading={isLoading}
          loadingDesktop={[...Array(10)].map((_, i) => (
            <UserSkeleton key={i} />
          ))}
          loadingMobile={[...Array(6)].map((_, i) => (
            <MobileUserSkeleton key={i} />
          ))}
          empty={emptyNode}
          footer={footerNode}
        />
      </div>

      {/* Silme onayı — ortak şifre + 2FA modalı (cihaz/EMQX ile aynı yapı) */}
      <PasswordConfirmModal
        open={deleteModal.open && !!deleteModal.user}
        onClose={closeDeleteModal}
        onConfirm={handleConfirmDelete}
        title="Kullanıcıyı Sil"
        subtitle={
          deleteModal.user
            ? `${deleteModal.user.name} ${deleteModal.user.lastName} – bu işlem geri alınamaz.`
            : "Bu işlem geri alınamaz."
        }
        confirmButtonText="Sil"
        isLoading={deleteLoading}
        twoFactorEnabled={twoFactorEnabled}
      />
    </div>
  );
};

export default UserListWithSearch;
